import { useEffect } from 'react';
import {
  ActivityIndicator,
  Alert,
  Platform,
  Pressable,
  StyleSheet,
  View,
  useWindowDimensions,
} from 'react-native';
import { observer } from 'mobx-react';
import { router } from 'expo-router';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';

import { ThemedText } from '@/components/ThemedText';
import { useStores } from '@/stores/StoreProvider';
import { colors } from '@/constants/colors';
import { workoutTranslations } from '@/constants/workoutTranslations';
import { workoutAssets } from '@/constants/workoutAssets';
import DefaultBackground from '@/components/DefaultBackground';
import TopbarLogo from '@/components/TopbarLogo';
import { FocusGuide } from '@/components/FocusGuide';
import { useScale } from '@/hooks/useScale';
import EquipmentIcon from '@/components/Icons/Equipment/EquipmentIcon';
import EquipmentTypes from '@/constants/equipmentTypes';

const isTvOS = Platform.isTV && Platform.OS === 'ios';

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function getResponsiveMetrics(width: number, height: number) {
  const shortestSide = Math.min(width, height);
  const layoutScale = clamp(shortestSide / 720, 0.5, 1.7);
  const typeScale = clamp(shortestSide / 720, 0.5, 1.65);
  const typeScaleWidth = clamp(width / 1024, 0.5, 1.65);
  const contentMaxWidth = clamp(width * 0.84, 920, 1560);
  const horizontalPadding = clamp(width * 0.05, 36, 180);

  return {
    layoutScale,
    typeScale,
    contentMaxWidth,
    horizontalPadding,
    logoHeight: clamp(48 * layoutScale, 44, 72),
    topBarVerticalPadding: clamp(24 * layoutScale, 20, 36),
    contentBottomPadding: clamp(40 * layoutScale, 32, 72),
    contentGap: clamp(22 * layoutScale, 20, 34),
    buttonGap: clamp(16 * layoutScale, 16, 28),
    buttonVerticalPadding: clamp(18 * layoutScale, 18, 28),
    buttonHorizontalPadding: clamp(28 * layoutScale, 28, 42),
    buttonBorderRadius: clamp(14 * layoutScale, 14, 24),
    buttonTextSize: clamp(22 * typeScale, 24, 34),
    titlePadding: clamp(32 * layoutScale, 14, 42),
    imageTitleSize: clamp(32 * typeScale, 18, 48),
    cardGap: clamp(18 * layoutScale, 16, 30),
    cardRadius: clamp(16 * layoutScale, 16, 28),
    statBoxPadding: clamp(24 * layoutScale, 14, 60),
    equipmentBoxPadding: clamp(32 * layoutScale, 14, 60),
    statBoxRadius: clamp(12 * layoutScale, 12, 22),
    statBoxGap: clamp(12 * layoutScale, 12, 22),
    statNumberSize: clamp(42 * typeScale, 40, 60),
    // On native , adjustsFontSizeToFit shrinks a long label
    // ("Exercises") only when it would overflow, so keep the label as large
    // as the number. On web that prop is a no-op, so use a smaller size that
    // always fits the narrow stat box. Mins + Exercises share this per platform.
    statLabelSize: clamp(
      (Platform.OS === 'web' ? 26 : 34) * typeScaleWidth,
      20,
      60,
    ),
    equipmentLabelSize: clamp(
      (Platform.OS === 'web' ? 20 : 26) * typeScaleWidth,
      14,
      60,
    ),
    equipmentGap: clamp(5 * layoutScale, 5, 10),
    equipmentIconSize: clamp(32 * layoutScale, 28, 52),
    textSize: clamp(16 * typeScale, 18, 24),
    smallTextSize: clamp(14 * typeScale, 16, 22),
    progressHeight: clamp(6 * layoutScale, 6, 10),
  };
}

function getWorkoutTitle(templateType: string | null): string {
  if (!templateType) return 'Workout';
  const translation =
    workoutTranslations[templateType as keyof typeof workoutTranslations] ??
    workoutTranslations['FALLBACK'];
  return translation.title;
}

function getWorkoutAssets(templateType: string | null) {
  if (!templateType) return workoutAssets['DEFAULT'];
  return (
    workoutAssets[templateType as keyof typeof workoutAssets] ??
    workoutAssets['DEFAULT']
  );
}

function TopBar({ onBack }: { onBack: () => void }) {
  const scale = useScale();
  return (
    // The Back button is pinned to the left edge while the actions below are
    // centered/right-aligned, so tvOS's geometric focus search never finds it.
    // A full-width guide catches the upward move and redirects it to Back.
    <FocusGuide autoFocus style={{ width: '100%' }}>
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          paddingVertical: 24 * scale,
          paddingHorizontal: 24 * scale,
        }}
      >
        <Pressable
          style={({ focused }) => [
            {
              position: 'absolute',
              left: 24 * scale,
              paddingVertical: 8 * scale,
              paddingHorizontal: 20 * scale,
              backgroundColor: 'rgba(255,255,255,0.15)',
              borderRadius: 20 * scale,
              borderWidth: 2,
              borderColor: 'transparent',
            },
            focused && {
              backgroundColor: 'rgba(255,255,255,0.3)',
              borderColor: '#fff',
            },
          ]}
          onPress={onBack}
        >
          <ThemedText
            style={{ color: '#fff', fontSize: 14 * scale, fontWeight: '600' }}
          >
            Back
          </ThemedText>
        </Pressable>
        <TopbarLogo />
      </View>
    </FocusGuide>
  );
}

function PreWorkoutScreen() {
  const { workoutPlayerStore, guidedWorkoutPlayerStore, workoutSessionStore } =
    useStores();
  const { width, height } = useWindowDimensions();
  const metrics = getResponsiveMetrics(width, height);
  const styles = useStyles(width, height);

  const handleCancel = () => {
    if (guidedWorkoutPlayerStore.isDownloading) {
      guidedWorkoutPlayerStore.cancelDownload();
    }
    workoutSessionStore.reset();
    router.replace('/(main)');
  };

  const templateType = workoutSessionStore.templateType;
  const assets = getWorkoutAssets(templateType);
  const imageUri = assets.pathInfo;
  const title = getWorkoutTitle(templateType);

  const mode = workoutSessionStore.mode;

  useEffect(() => {
    if (mode === 'none') {
      router.replace('/(main)');
    }
  }, [mode]);

  // Storage full: alert the user and leave the workout (no streaming fallback).
  const storageFull = guidedWorkoutPlayerStore.storageFull;
  useEffect(() => {
    if (!storageFull) return;
    const alertTitle = 'Not enough space';
    const alertMessage =
      'HiNote Life needs more storage space to prepare this workout. Free up space on your device, or delete the cache in Settings.';
    const leave = () => {
      guidedWorkoutPlayerStore.acknowledgeStorageFull();
      handleCancel();
    };
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined') {
        window.alert(`${alertTitle}\n\n${alertMessage}`);
      }
      leave();
    } else {
      Alert.alert(alertTitle, alertMessage, [{ text: 'OK', onPress: leave }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageFull]);

  if (mode === 'none' || storageFull) {
    return null;
  }

  const isCard = mode === 'card';
  const cardWorkout = workoutPlayerStore.workout;
  const guidedWorkout = guidedWorkoutPlayerStore.workout;
  const createError = isCard
    ? workoutPlayerStore.createError
    : guidedWorkoutPlayerStore.createError;

  // Loading: card waits for workout; guided waits unless there's already an error
  const cardIsLoading = !cardWorkout;
  const guidedIsLoading = !guidedWorkout && !createError;
  const isLoading = isCard ? cardIsLoading : guidedIsLoading;

  if (isLoading) {
    return (
      <View style={styles.container}>
        <DefaultBackground />
        <TopBar onBack={handleCancel} />
        <View style={styles.centeredContainer}>
          <ActivityIndicator size="large" color="#fff" />
          <ThemedText style={styles.description}>
            Preparing your workout...
          </ThemedText>
          {!!createError && (
            <ThemedText style={styles.errorText}>{createError}</ThemedText>
          )}
        </View>
      </View>
    );
  }

  if (createError) {
    return (
      <View style={styles.container}>
        <DefaultBackground />
        <TopBar onBack={handleCancel} />
        <View style={styles.centeredContainer}>
          <ThemedText type="title" lightColor="#fff" darkColor="#fff">
            Something went wrong
          </ThemedText>
          <ThemedText style={styles.errorText}>{createError}</ThemedText>
          <FocusGuide autoFocus style={styles.actionsGuide}>
            <Pressable
              style={({ focused }) => [
                styles.startButton,
                focused && styles.startButtonFocused,
              ]}
              onPress={handleCancel}
            >
              <ThemedText style={styles.startButtonText}>Go Back</ThemedText>
            </Pressable>
          </FocusGuide>
        </View>
      </View>
    );
  }

  // Derive ready-state data per mode
  let durationMinutes: number;
  let count: number;
  let countLabel: string;
  let equipment: string[];
  let onStart: () => void;
  let isDownloading = false;
  let downloadProgress = 0;

  const isResuming = isCard
    ? !!cardWorkout?.started_at
    : !!guidedWorkout?.started_at;

  if (isCard && cardWorkout) {
    const totalMs = cardWorkout.segments.reduce(
      (sum: number, s: any) => sum + s.segment_info.completion_time_ms,
      0,
    );
    durationMinutes = Math.ceil(totalMs / 1000 / 60);
    count = cardWorkout.totalExercises;
    countLabel = 'Exercises';
    equipment = [];
    onStart = () => {
      if (isResuming) {
        workoutPlayerStore.resumeWorkout();
      } else {
        workoutPlayerStore.startWorkout();
      }
      router.replace('/(workout)/player');
    };
  } else if (!isCard && guidedWorkout) {
    durationMinutes = Math.ceil(
      guidedWorkout.segment.completion_time_ms / 1000 / 60,
    );
    count = guidedWorkout.exerciseCount;
    countLabel = guidedWorkout.exerciseCountLabel;
    equipment = guidedWorkout.equipment;
    isDownloading = guidedWorkoutPlayerStore.isDownloading;
    downloadProgress = guidedWorkoutPlayerStore.downloadProgress;
    onStart = () => {
      if (isResuming) {
        guidedWorkoutPlayerStore.resumeWorkout();
      } else {
        guidedWorkoutPlayerStore.startWorkout();
      }
      router.replace('/(workout)/player');
    };
  } else {
    return null;
  }

  return (
    <View style={styles.container}>
      <DefaultBackground />
      <TopBar onBack={handleCancel} />
      <View style={styles.content}>
        <View style={styles.contentInner}>
          <View style={styles.infoCard}>
            <View style={styles.imageContainer}>
              <Image
                style={StyleSheet.absoluteFill}
                contentFit="cover"
                contentPosition="top"
                source={{ uri: imageUri }}
              />
              <LinearGradient
                colors={['transparent', 'rgba(0,0,0,0.65)']}
                style={[StyleSheet.absoluteFill, { top: '40%' }]}
              />
              <View style={styles.imageTitleContainer}>
                <ThemedText style={styles.imageTitle}>{title}</ThemedText>
              </View>
            </View>

            <View style={styles.statsContainer}>
              <View style={styles.statsRow}>
                <View style={styles.statBox}>
                  <ThemedText style={styles.statNumber}>
                    {durationMinutes}
                  </ThemedText>
                  <ThemedText style={styles.statLabel}>Mins</ThemedText>
                </View>
                <View style={styles.statBox}>
                  <ThemedText
                    numberOfLines={1}
                    adjustsFontSizeToFit
                    style={styles.statNumber}
                  >
                    {count}
                  </ThemedText>
                  <ThemedText
                    numberOfLines={1}
                    adjustsFontSizeToFit
                    style={styles.statLabel}
                  >
                    {countLabel}
                  </ThemedText>
                </View>
              </View>
              <View style={styles.equipmentBox}>
                {equipment.length === 0 ? (
                  <ThemedText style={styles.equipmentLabel}>
                    {isCard ? 'Equipment' : 'No equipment required'}
                  </ThemedText>
                ) : (
                  <>
                    <ThemedText style={styles.equipmentLabel}>
                      Equipment
                    </ThemedText>
                    <View style={styles.equipmentIcons}>
                      {equipment.map((e) => (
                        <EquipmentIcon
                          key={e}
                          name={e as EquipmentTypes}
                          width={metrics.equipmentIconSize}
                        />
                      ))}
                    </View>
                  </>
                )}
              </View>
            </View>
          </View>

          <FocusGuide autoFocus style={styles.actionsGuide}>
            {isDownloading ? (
              <View style={styles.downloadSection}>
                <View style={styles.downloadRow}>
                  <ThemedText style={styles.downloadText}>
                    {isTvOS
                      ? 'Preparing your workout...'
                      : `Preparing your workout ${downloadProgress}%`}
                  </ThemedText>
                  <Pressable
                    style={({ focused }) => [
                      styles.cancelButton,
                      focused && styles.cancelButtonFocused,
                    ]}
                    onPress={handleCancel}
                  >
                    <ThemedText style={styles.cancelButtonText}>
                      Cancel
                    </ThemedText>
                  </Pressable>
                </View>
                <View style={styles.progressBarTrack}>
                  <View
                    style={[
                      styles.progressBarFill,
                      { width: `${downloadProgress}%` },
                    ]}
                  />
                </View>
              </View>
            ) : (
              <View style={styles.buttonRow}>
                <Pressable
                  hasTVPreferredFocus
                  style={({ focused }) => [
                    styles.startButton,
                    focused && styles.startButtonFocused,
                  ]}
                  onPress={onStart}
                >
                  <ThemedText style={styles.startButtonText}>
                    {isResuming ? 'Resume Workout' : 'Start Workout'}
                  </ThemedText>
                </Pressable>
              </View>
            )}
          </FocusGuide>
        </View>
      </View>
    </View>
  );
}

export default observer(PreWorkoutScreen);

function useStyles(width: number, height: number) {
  const metrics = getResponsiveMetrics(width, height);

  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.brand.primary,
    },
    centeredContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: metrics.horizontalPadding,
    },
    description: {
      marginTop: 12 * metrics.layoutScale,
      fontSize: metrics.textSize,
      color: 'rgba(255, 255, 255, 0.85)',
      textAlign: 'center',
    },
    errorText: {
      marginTop: 16 * metrics.layoutScale,
      fontSize: metrics.smallTextSize,
      color: '#ffb3b3',
      textAlign: 'center',
    },
    content: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: metrics.horizontalPadding,
      paddingBottom: metrics.contentBottomPadding,
    },
    contentInner: {
      width: '100%',
      maxWidth: metrics.contentMaxWidth,
      gap: metrics.contentGap,
    },
    downloadSection: {
      width: '100%',
      gap: 10 * metrics.layoutScale,
    },
    downloadRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    downloadText: {
      fontSize: clamp(18 * metrics.typeScale, 20, 30),
      fontWeight: '600',
      color: '#fff',
    },
    progressBarTrack: {
      height: metrics.progressHeight,
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
      borderRadius: metrics.progressHeight / 2,
      overflow: 'hidden',
    },
    progressBarFill: {
      height: '100%',
      backgroundColor: '#fff',
      borderRadius: metrics.progressHeight / 2,
    },
    // Full-width guide so a downward move from the left-pinned Back button has
    // something to land on, whatever the actions inside are aligned to.
    actionsGuide: {
      width: '100%',
      alignItems: 'center',
    },
    buttonRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: metrics.buttonGap,
      width: '100%',
    },
    startButton: {
      paddingVertical: metrics.buttonVerticalPadding,
      paddingHorizontal: metrics.buttonHorizontalPadding,
      backgroundColor: '#fff',
      borderRadius: metrics.buttonBorderRadius,
      borderWidth: 2,
      borderColor: '#fff',
      alignItems: 'center',

      // white shadow
      shadowColor: '#fff',
      shadowOffset: {
        width: 0,
        height: 0,
      },
      shadowOpacity: 0.4,
      shadowRadius: 8 * metrics.layoutScale,
    },
    startButtonFocused: {
      borderColor: colors.brand.primary,
      backgroundColor: 'rgba(255, 255, 255, 0.85)',
    },
    startButtonText: {
      color: colors.brand.primary,
      fontSize: metrics.buttonTextSize,
      fontWeight: '700',
    },
    cancelButton: {
      paddingVertical: metrics.buttonVerticalPadding,
      paddingHorizontal: metrics.buttonHorizontalPadding,
      backgroundColor: '#fff',
      borderRadius: metrics.buttonBorderRadius,
      borderWidth: 2,
      borderColor: '#fff',
      alignItems: 'center',
      shadowColor: '#fff',
      shadowOffset: { width: 0, height: 0 },
      shadowOpacity: 0.4,
      shadowRadius: 8 * metrics.layoutScale,
    },
    cancelButtonFocused: {
      borderColor: colors.brand.primary,
      backgroundColor: 'rgba(255, 255, 255, 0.85)',
    },
    cancelButtonText: {
      color: colors.brand.primary,
      fontSize: metrics.buttonTextSize,
      fontWeight: '600',
    },
    infoCard: {
      flexDirection: 'row',
      width: '100%',
      gap: metrics.cardGap,
    },
    imageContainer: {
      flex: 1.12,
      aspectRatio: 16 / 10,
      position: 'relative',
      borderRadius: metrics.cardRadius,
      justifyContent: 'flex-end',
      overflow: 'hidden',
    },
    imageTitleContainer: {
      padding: metrics.titlePadding,
    },
    imageTitle: {
      color: '#fff',
      fontSize: metrics.imageTitleSize,
      fontWeight: '700',
    },
    statsContainer: {
      flex: 0.88,
      gap: metrics.cardGap,
      justifyContent: 'space-between',
    },
    statsRow: {
      flexDirection: 'row',
      gap: metrics.cardGap,
      flex: 1,
    },
    statBox: {
      flex: 1,
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      borderRadius: metrics.statBoxRadius,
      justifyContent: 'center',
      alignItems: 'center',
      padding: metrics.statBoxPadding,
      gap: metrics.statBoxGap,
    },
    statNumber: {
      color: '#fff',
      fontSize: metrics.statNumberSize,
      fontWeight: '700',
    },
    statLabel: {
      color: '#fff',
      fontSize: metrics.statLabelSize,
      fontWeight: '600',
    },
    equipmentBox: {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      borderRadius: metrics.statBoxRadius,
      padding: metrics.equipmentBoxPadding,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    equipmentLabel: {
      color: '#fff',
      fontSize: metrics.equipmentLabelSize,
      fontWeight: '600',
    },
    equipmentIcons: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: metrics.equipmentGap,
    },
  });
}
